/* eslint-disable jsx-a11y/alt-text */
import { forwardRef, useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import ICONS from "../../../assets/Icons";

/**
 * @typedef {("verified" | "error" | "typing" | "default")} TypeVariants
 * @typedef {("big" | "small")} FontVariants
 */
/**
 * @typedef PropType
 * @type {object}
 * @property {string} placeholder - Text to show in the placeholder
 * @property {TypeVariants} variant - one of "error", "verified", "typing", or "default"
 * @property {string} bgColor - background colour of element that this input field is on top of (var only)
 * @property {string} fgColor - The foreground colour of this input field. (var only)
 * @property {string} adornment - The icon to show in the input field.
 * - If this is not given, no icon will be shown in typing and default states and the
 * default error and verified icons will be shown in their respective variants
 * - If this is the empty string ("") then no icon will be shown whatsoever.
 * @property {React.FormEventHandler<HtmlElement>} [onAdornmentClick] - This function is called when the adornment is clicked.
 * @property {React.FormEventHandler<HTMLInputElement>} [onInput] - This function is given to "onInput" of input tag
 * @property {string} [value] - The value attribute of the input tag
 * @property {string} [name] - The value of the name attribute of the underlying <input>
 * @property {FontVariants} [fontKind="big"] - Use "big" font or "small" font. defaults to big.
 * @property {Boolean} isDisabled - Whether this input field is disabled.
 * @property {Boolean} isFullWidth - Whether this input field is full width.
 * @property {React.FocusEventHandler<HTMLInputElement>} [onFocus]
 * @property {React.FocusEventHandler<HTMLInputElement>} [onBlur]
 * @property {boolean} needsColor Should the field be colored.
 * @property {number} [upperLimit] Upper limit, if undefined then there is no upper limit
 */
/**
 * @type {React.ForwardRefRenderFunction<HtmlHTMLInputElement, PropType>}
 */
export default forwardRef(function (
  {
    placeholder,
    variant,
    bgColor,
    fgColor,
    adornment,
    onInput,
    onFocus,
    onBlur,
    value,
    fontKind,
    name,
    isDisabled,
    isFullWidth,
    onAdornmentClick,
    needsColor = false,
    upperLimit = undefined,
  },
  inputBoxRef,
) {
  const [variantInfo, setVariantInfo] = useState({
    color: "var(--text-primary)",
    adornment: null,
    fontKind: fontKind === "small" ? "var(--font-xs)" : "var(--font-md)",
    adornmentDimension:
      fontKind && fontKind === "small" ? "var(--icon-sm)" : "var(--icon-md)",
  });
  const [oldVariant, setOldVariant] = useState({});

  useEffect(() => {
    let vi;
    switch (variant) {
      case "verified":
        if (needsColor)
          vi = {
            ...variantInfo,
            color: "var(--positive-act)",
            adornment: ICONS.VERIFIED_WITH_WHITE_CIRCLE_GREEN,
          };
        else
          vi = {
            ...variantInfo,
            color: "var(--positive-act)",
            // color: fgColor ?? "var(--text-primary)",
            adornment: ICONS.VERIFIED_WITH_WHITE_CIRCLE_GREEN,
          };
        break;
      case "error":
        if (needsColor)
          vi = {
            ...variantInfo,
            color: "var(--negative-act)",
            adornment: ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE,
          };
        else
          vi = {
            ...variantInfo,
            // color: "var(--negative-act)",
            color: fgColor ?? "var(--text-primary)",
            adornment: ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE,
          };

        break;
      case "typing":
        vi = {
          ...variantInfo,
          color: "var(--pending)",
          adornment: null,
        };
        break;
      default:
        vi = {
          ...variantInfo,
          color: fgColor ?? "var(--text-primary)",
          adornment: null,
        };
        break;
    }
    setVariantInfo(vi);
    setOldVariant(vi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  useEffect(() => {
    setVariantInfo((i) => {
      let vi = {
        ...i,
        fontKind:
          fontKind && fontKind === "small"
            ? "var(--font-xs)"
            : "var(--font-md)",
        adornmentDimension:
          fontKind && fontKind === "small"
            ? "var(--icon-sm)"
            : "var(--icon-md)",
      };
      setOldVariant(vi);
      return vi;
    });
  }, [fontKind]);

  useEffect(() => {
    if (value) {
      setIsFocused(true);
    }
  }, [value]);

  const iRef = useRef(null);
  inputBoxRef ??= iRef;

  const [isFocused, setIsFocused] = useState(false);
  const placeHolderRef = useRef(null);
  return (
    <div
      onClick={(e) => {
        if (isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (inputBoxRef?.current?.value) {
          setIsFocused(true);
          return;
        }
        setIsFocused(true);
        inputBoxRef?.current?.focus();
      }}
      className={`border  flex flex-row items-center py-padding-sm px-padding-md justify-between gap-gap-xs rounded-full w-full relative ${
        isFullWidth ? "" : "max-w-input-max-width min-w-input-min-width"
      }`}
      role="textbox"
      style={{
        fontSize: variantInfo.fontKind,
        borderColor: variantInfo.color,
        opacity: isDisabled ? "var(--light-opacity)" : "var(--base-opacity)",
        cursor: isDisabled ? "not-allowed" : "default",
      }}
    >
      <div
        className="transition-all px-padding-xs"
        ref={placeHolderRef}
        style={{
          userSelect: "none",
          backgroundColor: bgColor,
          color: variantInfo.color,
          transform: isFocused
            ? "translateY(calc(-2*var(--padding-sm)))"
            : "translateY(0)",
          fontSize: isFocused ? "var(--font-xs)" : variantInfo.fontKind,
          position: "absolute",
        }}
      >
        {placeholder}
      </div>
      <div
        style={{
          color: "var(--text-primary)",
        }}
      >
        INR
      </div>
      <NumericFormat
        name={name}
        className="w-full"
        style={{
          fontSize: variantInfo.fontKind,
          color: fgColor ?? variantInfo.color,
          backgroundColor: bgColor,
          cursor: isDisabled ? "not-allowed" : "default",
          userSelect: isDisabled ? "none" : "auto",
        }}
        isAllowed={(values) => {
          if (!values.value) return true;
          const { value } = values;
          if (value[0] === "0") {
            return false;
          }
          if (+value > upperLimit && upperLimit !== undefined) return false;
          return true;
        }}
        disabled={isDisabled}
        value={value}
        valueIsNumericString
        getInputRef={inputBoxRef}
        thousandSeparator=","
        allowNegative={false}
        decimalScale={2}
        thousandsGroupStyle="lakh"
        onValueChange={(v, _) => {
          if (typeof onInput === "function") onInput(v.value);
        }}
        onFocus={(e) => {
          setIsFocused(true);
          // if (!["default", "error"].includes(variant)) {
          if (variant !== "default") {
            setVariantInfo({
              ...variantInfo,
            });
          } else {
            if (needsColor) {
              setVariantInfo({
                ...variantInfo,
                color: "var(--pending)",
              });
            } else {
              setVariantInfo({ ...variantInfo });
            }
          }
        }}
        onBlur={() => {
          setVariantInfo(oldVariant);
          if (inputBoxRef?.current?.value) {
            return;
          }
          setIsFocused(false);
        }}
      />
      {adornment !== "" && (
        <div
          onClick={onAdornmentClick ?? ((e) => {})}
          style={{
            width: variantInfo.adornmentDimension,
            height: variantInfo.adornmentDimension,
            cursor: onAdornmentClick ? "pointer" : "default",
          }}
          className="flex-shrink-0 ml-margin-sm"
        >
          <img
            style={{
              maxWidth: variantInfo.adornmentDimension,
              height: variantInfo.adornmentDimension,
            }}
            src={adornment ?? variantInfo.adornment}
            className="flex-shrink-0"
          />
        </div>
      )}
    </div>
  );
});
